/**
 * InvalidAssociationsTable — invalid association mapping table for the CSV
 * Import Review summary.
 *
 * Extracted from AssociationTables.tsx to keep that file under 300 lines.
 */

import { AlertTriangle } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Table, TableCard } from '@/lib/base/table';
import type { AssociationsData, CsvImport } from '@/lib/csv-import/api';

import { getEntityTypeLabel } from '../../utils/entityTypeLabels';
import { getFileDisplayName } from '../../utils/fileDisplayName';
import {
  GROUP_CAP_INCREMENT,
  INITIAL_GROUP_CAP,
  prettifyColumnName,
} from './AssociationTables';

interface InvalidAssociationRow {
  id: string;
  sourceFile: string;
  sourceColumn: string;
  targetEntityLabel: string;
  targetProperty: string;
  message: string;
}

export interface InvalidAssociationsTableProps {
  invalidAssociations: AssociationsData['invalid_associations'];
  importData: CsvImport;
}

/**
 * Renders invalid association mappings — mappings whose `target_property`
 * doesn't exist on `target_entity_type_name`. Unlike UnmatchedAssociationsTable
 * (a per-row miss), this is a per-mapping problem: every row using the mapping
 * is skipped, so there's one row per distinct (source_column, target_property)
 * pair rather than one per CSV row. Uses an ERROR (not warning) treatment —
 * this is a config problem the user should fix, not an expected data gap.
 */
export function InvalidAssociationsTable({
  invalidAssociations,
  importData,
}: InvalidAssociationsTableProps) {
  const [displayCap, setDisplayCap] = useState(INITIAL_GROUP_CAP);
  const handleShowMore = useCallback(() => {
    setDisplayCap(prev => prev + GROUP_CAP_INCREMENT);
  }, []);

  const tableRows = useMemo<InvalidAssociationRow[]>(() => {
    const groups = new Map<string, InvalidAssociationRow>();
    for (const inv of invalidAssociations ?? []) {
      const sourceFile = getFileDisplayName(
        String(inv.source_file_id),
        importData.uploaded_files
      );
      const sourceColumn = prettifyColumnName(inv.source_column);
      const targetEntityLabel = getEntityTypeLabel(inv.target_entity_type_name);
      const targetProperty = prettifyColumnName(inv.target_property);
      // Key off the raw identifiers, not the rendered labels: two uploads that
      // share a filename (or two entity types that resolve to the same label)
      // must not collapse into one row, or the table would show fewer rows than
      // the banner count.
      const key = `${inv.source_file_id}|${inv.source_column}|${inv.target_entity_type_name}|${inv.target_property}`;
      if (groups.has(key)) {
        continue;
      }
      groups.set(key, {
        id: key,
        sourceFile,
        sourceColumn,
        targetEntityLabel,
        targetProperty,
        message: `Column "${sourceColumn}" is mapped to match on "${targetProperty}", which doesn't exist on ${targetEntityLabel}. All of these associations will be skipped. Go back to the Mapping step to fix or remove this mapping.`,
      });
    }
    return Array.from(groups.values());
  }, [invalidAssociations, importData.uploaded_files]);

  const isCapped = tableRows.length > displayCap;
  const visibleRows = useMemo(
    () => (isCapped ? tableRows.slice(0, displayCap) : tableRows),
    [tableRows, displayCap, isCapped]
  );

  if (tableRows.length === 0) {
    return (
      <p className='text-xs text-fg-tertiary text-center py-2'>
        No invalid associations to display.
      </p>
    );
  }

  return (
    <div className='border border-utility-error-200 rounded-lg overflow-hidden bg-bg-primary'>
      <TableCard.ScrollArea>
        <Table size='sm' aria-label='Invalid association mappings'>
          <Table.Header>
            <Table.Row>
              <Table.Head label='Source' isRowHeader />
              <Table.Head label='Column' />
              <Table.Head label='Issue' />
            </Table.Row>
          </Table.Header>
          {/* Static body to match the static header (see PendingAssociationsTable
              for the explanation of why we don't use id/items in static mode). */}
          <Table.Body>
            {visibleRows.map(row => (
              <Table.Row key={row.id}>
                <Table.Cell>{row.sourceFile}</Table.Cell>
                <Table.Cell>{row.sourceColumn}</Table.Cell>
                <Table.Cell className='min-w-[280px] text-utility-error-700 whitespace-normal break-words'>
                  {row.message}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </TableCard.ScrollArea>
      {isCapped && (
        <div className='border-t border-border-secondary px-4 py-2 text-center'>
          <Button color='link-gray' size='sm' onClick={handleShowMore}>
            Show {Math.min(GROUP_CAP_INCREMENT, tableRows.length - displayCap)}{' '}
            more of {tableRows.length} rows
          </Button>
        </div>
      )}
    </div>
  );
}

export interface InvalidAssociationsBannerProps {
  invalidAssociations: AssociationsData['invalid_associations'];
  importData: CsvImport;
}

/**
 * Error-styled wrapper around InvalidAssociationsTable — shared between
 * SummaryView and LargeImportSummary so the banner markup lives in one place.
 */
export function InvalidAssociationsBanner({
  invalidAssociations,
  importData,
}: InvalidAssociationsBannerProps) {
  const count = invalidAssociations?.length ?? 0;
  if (count === 0) {
    return null;
  }

  return (
    <div className='p-3 bg-utility-error-50 border border-utility-error-200 rounded-lg'>
      <div className='flex items-start gap-2 mb-2'>
        <AlertTriangle
          aria-hidden={true}
          className='w-4 h-4 text-utility-error-600 flex-shrink-0 mt-0.5'
        />
        <div className='flex-1 min-w-0'>
          <p className='text-sm font-medium text-utility-error-800'>
            {count} invalid association mapping{count === 1 ? '' : 's'}
          </p>
          <p className='text-xs text-utility-error-700 mt-1'>
            These mappings reference a property that doesn&apos;t exist on the
            target record type. Associations using them will be skipped.
          </p>
        </div>
      </div>
      <InvalidAssociationsTable
        invalidAssociations={invalidAssociations}
        importData={importData}
      />
    </div>
  );
}
